import {
  DataStatus,
  Form,
  FormField,
  FormSubmitHandler,
  Label,
  showNotification,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {head} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  useCreateSignatureMutation,
  useListSignaturesQuery,
  useRemoveSignatureMutation,
  useUpdateSignatureMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError} from 'shared/web';

import {MyProfileFooter} from '../../components/MyProfileFooter';
import {MyProfileTemplate} from '../../components/MyProfileTemplate/MyProfileTemplate';

type FormValues = {
  signature: string;
};

export function EmailSignature() {
  const {data: signatures, isLoading, isError} = useListSignaturesQuery();

  const [createSignature] = useCreateSignatureMutation();
  const [updateSignature] = useUpdateSignatureMutation();
  const [removeSignature] = useRemoveSignatureMutation();

  const usedSignature = head(signatures ?? []);

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    if (usedSignature && isNilOrEmpty(values.signature)) {
      await removeSignature({id: usedSignature.id})
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
          reset(undefined, {keepValues: true});
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );

      return;
    }

    if (usedSignature) {
      await updateSignature({
        id: usedSignature.id,
        updateSignatureRequestBody: values,
      })
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('general.notifications.successfullyCreated'));
          reset(undefined, {keepValues: true});
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );

      return;
    }

    return createSignature({updateSignatureRequestBody: values})
      .unwrap()
      .then(() => {
        reset(undefined, {keepValues: true});
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues = {
    signature: usedSignature?.signature,
  };

  return (
    <MyProfileTemplate
      header={{
        title: i18n.t('page.myProfile.labels.emailSignature'),
        breadcrumbs: [
          {label: i18n.t('entity.user.labels.myProfile'), isCurrentPage: true},
          {label: i18n.t('page.myProfile.labels.emailSignature'), isCurrentPage: true},
        ],
      }}
    >
      <DataStatus isLoading={isLoading} isError={isError} spacing={10}>
        <Form<FormValues>
          shouldWatchForUnsavedChanges
          onSubmit={handleSubmit}
          defaultValues={defaultValues}
        >
          {(control, formApi) => (
            <VStack spacing={2} maxWidth={200}>
              <Label>{i18n.t('page.myProfile.labels.emailSignatureDescription')}</Label>

              <FormField
                type="textarea"
                name="signature"
                label={i18n.t('page.myProfile.labels.emailSignature')}
                control={control}
                minRows={3}
              />

              <MyProfileFooter
                actions={[
                  {
                    type: 'button',
                    onClick: () => formApi.reset(defaultValues),
                    title: i18n.t('general.actions.discardChanges'),
                    variant: 'secondary',
                  },
                  {
                    type: 'form-button',
                    control,
                    buttonType: 'submit',
                    title: usedSignature
                      ? i18n.t('general.actions.saveChanges')
                      : i18n.t('general.actions.create'),
                  },
                ]}
              />
            </VStack>
          )}
        </Form>
      </DataStatus>
    </MyProfileTemplate>
  );
}
